import { HymnFormatInput, extractVerseLines } from '../utils';

/**
 * Escapes special characters for XML / OpenLyrics compliance
 */
export function escapeXml(unsafe: string): string {
  if (!unsafe || typeof unsafe !== 'string') return '';
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '\'': return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
}

/**
 * Sanitizes a filename for cross-platform OS filesystem downloads
 */
export function sanitizeFilename(name: string): string {
  return (name || 'hymn')
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Builds EasyWorship slide format (.txt)
 */
export function buildEasyWorshipTxt(item: HymnFormatInput): string {
  const numStr = item.number != null ? String(item.number).padStart(3, '0') : '';
  const displayTitle = numStr ? `${numStr} - ${item.title}` : (item.title || 'Hymn');
  const author = item.section || item.section_code || "Chants d'Espérance";

  let rawVerses: any = item.verses;
  if (typeof rawVerses === 'string') {
    try {
      rawVerses = JSON.parse(rawVerses);
    } catch {
      rawVerses = null;
    }
  }

  const slideBlocks: string[] = [];
  if (Array.isArray(rawVerses) && rawVerses.length > 0) {
    let vCount = 1;
    let cCount = 1;
    rawVerses.forEach((v: any) => {
      const vType = (v.type || 'verse').toLowerCase();
      let header = '';
      if (vType === 'chorus' || vType === 'refrain' || vType === 'kè' || vType === 'choeur') {
        header = cCount > 1 ? `Chorus ${cCount}` : 'Chorus';
        cCount++;
      } else if (vType === 'bridge' || vType === 'pont') {
        header = 'Bridge';
      } else if (vType === 'tag' || vType === 'ending' || vType === 'fin') {
        header = 'Tag';
      } else {
        const vNum = v.number != null ? v.number : vCount++;
        header = `Verse ${vNum}`;
      }
      const lines = extractVerseLines(v);
      if (lines.length > 0) {
        slideBlocks.push(`${header}\n${lines.join('\n')}`);
      }
    });
  } else if (item.full_text) {
    slideBlocks.push(`Verse 1\n${item.full_text.trim()}`);
  }

  return [
    `Title: ${displayTitle}`,
    `Author: ${author}`,
    'Copyright: Public Domain',
    item.number != null ? `Song Number: ${item.number}` : '',
    item.category ? `Theme: ${item.category}` : '',
    item.source_data ? `Source: ${item.source_data}` : '',
    '',
    slideBlocks.join('\n\n')
  ].filter((l) => l !== undefined && l !== '').join('\n') + '\n';
}

/**
 * Builds standard OpenLyrics 0.8 XML (.xml) for OpenLP, ProPresenter, SongShow Plus
 */
export function buildOpenLyricsXml(item: HymnFormatInput): string {
  const numStr = item.number != null ? String(item.number).padStart(3, '0') : '';
  const displayTitle = numStr ? `${numStr} - ${item.title}` : (item.title || 'Hymn');
  const author = item.section || item.section_code || "Chants d'Espérance";
  const now = new Date().toISOString();

  let rawVerses: any = item.verses;
  if (typeof rawVerses === 'string') {
    try {
      rawVerses = JSON.parse(rawVerses);
    } catch {
      rawVerses = null;
    }
  }

  const verseBlocks: string[] = [];
  if (Array.isArray(rawVerses) && rawVerses.length > 0) {
    let vCount = 1;
    let cCount = 1;
    let bCount = 1;
    let eCount = 1;
    rawVerses.forEach((v: any) => {
      const vType = (v.type || 'verse').toLowerCase();
      let vName = '';
      if (vType === 'chorus' || vType === 'refrain' || vType === 'kè' || vType === 'choeur') {
        vName = `c${cCount++}`;
      } else if (vType === 'bridge' || vType === 'pont') {
        vName = `b${bCount++}`;
      } else if (vType === 'tag' || vType === 'ending' || vType === 'fin') {
        vName = `e${eCount++}`;
      } else {
        const vNum = v.number != null ? v.number : vCount++;
        vName = `v${vNum}`;
      }
      const lines = extractVerseLines(v);
      if (lines.length > 0) {
        const escLines = lines
          .map((l) => escapeXml(l))
          .join('<br/>\n        ');
        verseBlocks.push(`    <verse name="${escapeXml(vName)}">\n      <lines>\n        ${escLines}\n      </lines>\n    </verse>`);
      }
    });
  } else if (item.full_text) {
    const escLines = item.full_text.split(/\r?\n/)
      .map((l) => escapeXml(l.trim()))
      .filter((l) => l.length > 0)
      .join('<br/>\n        ');
    if (escLines.length > 0) {
      verseBlocks.push(`    <verse name="v1">\n      <lines>\n        ${escLines}\n      </lines>\n    </verse>`);
    }
  }

  return '<?xml version="1.0" encoding="UTF-8"?>\n' +
    `<song xmlns="http://openlyrics.info/namespace/2009/song" version="0.8" createdIn="Chants d'Espérance API" modifiedDate="${now}">\n` +
    '  <properties>\n' +
    '    <titles>\n' +
    `      <title>${escapeXml(displayTitle)}</title>\n` +
    `      <title>${escapeXml(item.title || '')}</title>\n` +
    '    </titles>\n' +
    '    <authors>\n' +
    `      <author>${escapeXml(author)}</author>\n` +
    '    </authors>\n' +
    '    <songbooks>\n' +
    `      <songbook name="${escapeXml(author)}"${item.number != null ? ` entry="${escapeXml(String(item.number))}"` : ''}/>\n` +
    '    </songbooks>\n' +
    (item.category ? `    <themes>\n      <theme>${escapeXml(item.category)}</theme>\n    </themes>\n` : '') +
    (item.source_data ? `    <comments>\n      <comment>${escapeXml(item.source_data)}</comment>\n    </comments>\n` : '') +
    '    <copyright>Public Domain</copyright>\n' +
    '  </properties>\n' +
    '  <lyrics>\n' +
    verseBlocks.join('\n') + '\n' +
    '  </lyrics>\n' +
    '</song>\n';
}

/**
 * Builds clean plain text / CCLI format (.txt)
 */
export function buildCcliTxt(item: HymnFormatInput): string {
  const numStr = item.number != null ? String(item.number).padStart(3, '0') : '';
  const displayTitle = numStr ? `${numStr} - ${item.title}` : (item.title || 'Hymn');
  const author = item.section || item.section_code || "Chants d'Espérance";

  let rawVerses: any = item.verses;
  if (typeof rawVerses === 'string') {
    try {
      rawVerses = JSON.parse(rawVerses);
    } catch {
      rawVerses = null;
    }
  }

  const blocks: string[] = [];
  let verseIndex = 1;
  let chorusIndex = 1;

  const verses = (Array.isArray(rawVerses) && rawVerses.length > 0)
    ? rawVerses
    : (item.full_text ? [{ type: 'verse', content: item.full_text }] : []);

  verses.forEach((v: any) => {
    const vType = (v.type || 'verse').toLowerCase();
    let header = '';
    if (vType === 'chorus' || vType === 'refrain' || vType === 'kè' || vType === 'choeur') {
      header = chorusIndex > 1 ? `[Chorus ${chorusIndex}]` : '[Chorus]';
      chorusIndex++;
    } else if (vType === 'bridge' || vType === 'pont') {
      header = '[Bridge]';
    } else if (vType === 'tag' || vType === 'ending' || vType === 'fin') {
      header = '[Tag]';
    } else {
      const vNum = v.number != null ? v.number : verseIndex++;
      header = `[Verse ${vNum}]`;
    }
    const lines = extractVerseLines(v);
    if (lines.length > 0) {
      blocks.push(`${header}\n${lines.join('\n')}`);
    }
  });

  const lines = [
    displayTitle,
    `Collection: ${author}`,
    item.category ? `Category: ${item.category}` : '',
    'CCLI Song No: N/A',
    'Public Domain',
    '',
    blocks.join('\n\n'),
  ];

  if (item.source_data) {
    lines.push('', `Source : ${item.source_data}`);
  }

  return lines.filter((l) => l !== undefined && l !== '').join('\n') + '\n';
}

/**
 * Triggers browser download of text/xml file
 */
export function triggerFileDownload(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => { URL.revokeObjectURL(url); }, 1500);
}
